import type { DateRangePresetId } from "@/components/ui/v2/date-range-picker";
import { DateRangePicker } from "@/components/ui/v2/date-range-picker";
import { Label } from "@/components/ui/v2/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/v2/select";
import { Switch } from "@/components/ui/v2/switch";
import type { TableToolbarFilterDefinition } from "@/features/integrations/types";
import { cn } from "@/lib/utils/cn";
import { formatLocalDateOnly } from "@/utils/dateUtils";

interface FilterControlProps {
  filter: TableToolbarFilterDefinition;
  value: unknown;
  onChange: (value: unknown) => void;
  t: (key: string) => string;
  dateRangePickerOptions?: {
    minDate?: Date;
    maxDate?: Date;
    allowedPresetIds?: readonly DateRangePresetId[];
  };
}

/**
 * FilterControl - renders a filter input based on filter type.
 * Supports Switch, Select, and Date filter types.
 */
function FilterControl({
  filter,
  value,
  onChange,
  t,
  dateRangePickerOptions,
}: FilterControlProps) {
  if (filter.type === "Switch" || filter.type === "Boolean") {
    const checked = value === true || value === "true";

    if (filter.renderAs === "chip") {
      return (
        <button
          type="button"
          onClick={() => onChange(!checked)}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium transition-colors",
            checked
              ? "[background:var(--badge-info-bg)] [border-color:var(--badge-info-border)] [color:var(--badge-info-text)]"
              : "border-border bg-transparent text-muted-foreground hover:bg-accent",
          )}
        >
          {t(filter.label)}
        </button>
      );
    }

    return (
      <div className="flex items-center gap-2">
        <Switch
          id={`toolbar-filter-${filter.key}`}
          checked={checked}
          onCheckedChange={(checked) => onChange(checked)}
        />
        <Label
          htmlFor={`toolbar-filter-${filter.key}`}
          className="cursor-pointer text-sm"
        >
          {t(filter.label)}
        </Label>
      </div>
    );
  }

  if (filter.type === "Select" && filter.options?.length) {
    const strValue = value != null ? String(value) : filter.options[0]?.value;
    return (
      <div className="flex items-center gap-2">
        <Select
          value={strValue}
          onValueChange={(v) => onChange(v)}
          name={filter.key}
        >
          <SelectTrigger
            id={`toolbar-filter-${filter.key}`}
            size="sm"
            className="w-[180px]"
          >
            <SelectValue placeholder={t("common.select")} />
          </SelectTrigger>
          <SelectContent>
            {filter.options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {t(opt.label)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (filter.type === "Date") {
    const dateValue =
      value instanceof Date
        ? formatLocalDateOnly(value)
        : typeof value === "string"
          ? value.slice(0, 10)
          : "";
    return (
      <div className="flex items-center gap-2">
        <Label htmlFor={`toolbar-filter-${filter.key}`} className="text-sm">
          {t(filter.label)}
        </Label>
        <input
          id={`toolbar-filter-${filter.key}`}
          type="date"
          className="border-input bg-background h-9 rounded-md border px-3 py-2 text-sm"
          value={dateValue}
          onChange={(e) => onChange(e.target.value || undefined)}
        />
      </div>
    );
  }

  if (filter.type === "DateRange") {
    const rangeValue =
      value != null && typeof value === "object" && "startDate" in value
        ? (value as { startDate?: string; endDate?: string })
        : {};
    const fromStr = rangeValue.startDate ?? "";
    const toStr = rangeValue.endDate ?? "";
    return (
      <div className="flex items-center gap-2">
        <Label className="text-sm shrink-0">{t(filter.label)}</Label>
        <DateRangePicker
          key={`${fromStr}-${toStr}`}
          initialDateFrom={fromStr || undefined}
          initialDateTo={toStr || undefined}
          maxDate={dateRangePickerOptions?.maxDate}
          minDate={dateRangePickerOptions?.minDate}
          allowedPresetIds={dateRangePickerOptions?.allowedPresetIds}
          onUpdate={({ range }) => {
            onChange({
              startDate: range?.from ? formatLocalDateOnly(range.from) : undefined,
              endDate: range?.to ? formatLocalDateOnly(range.to) : undefined,
            });
          }}
        />
      </div>
    );
  }

  return null;
}

export { FilterControl };
